import { FocusableOption, FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
    Optional,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    CanColor,
    CanDisable,
    CanDisableRipple,
    MatRipple,
    mixinColor,
    mixinDisabled,
    mixinDisableRipple,
} from '@angular/material/core';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import Debug from 'debug';
import { MAIN_FONT } from '../style/fonts';
import { CanIcon, CanIconCtor, mixinIcon } from './../style/CanIcon';
import { CanSize, CanSizeCtor, mixinSize } from './../style/CanSize';

/** @docs-private */
export type Constructor<T> = new (...args: any[]) => T;

/**
 * This is a permissive type for abstract class constructors.
 * @docs-private
 */
export type AbstractConstructor<T = object> = abstract new (...args: any[]) => T;

type CanColorCtor = Constructor<CanColor> & AbstractConstructor<CanColor>;
type CanDisableCtor = Constructor<CanDisable> & AbstractConstructor<CanDisable>;
type CanDisableRippleCtor = Constructor<CanDisableRipple> & AbstractConstructor<CanDisableRipple>;

class AumButtonBase {
    constructor(public _elementRef: ElementRef) {}
}
export type BooleanInput = string | boolean | null | undefined;

const debug = Debug('aumico:aumico-lib-core-fe:AumButton');

const _AumButtonMixinBase: CanDisableRippleCtor &
    CanDisableCtor &
    CanColorCtor &
    CanSizeCtor &
    CanIconCtor &
    typeof AumButtonBase = mixinColor(mixinDisabled(mixinSize(mixinIcon(mixinDisableRipple(AumButtonBase)), 'medium')));

/**
 * List of classes to add to AumButton instances based on host attributes to
 * style as different variants.
 */
const BUTTON_HOST_ATTRIBUTES = ['aum-button', 'aum-icon-button'];

/** Default color palette for round buttons (mat-fab and mat-mini-fab) */
const DEFAULT_BUTTON_COLOR = 'basic';
const DEFAULT_BUTTON_ICON = 'plus';

@Component({
    selector: `button[aum-button], button[aum-icon-button]`,
    exportAs: 'aumButton',
    host: {
        '[attr.disabled]': 'disabled || null',
        '[class.aum-button-disabled]': 'disabled',
        '[class._mat-animation-noopable]': '_animationMode === "NoopAnimations"',
        class: 'mat-focus-indicator',
    },
    template: `
        <span><ng-content></ng-content></span>
        <div
            matRipple
            class="mat-button-ripple"
            [class.mat-button-ripple-round]="isIconButton"
            [matRippleDisabled]="_isRippleDisabled()"
            [matRippleCentered]="isIconButton"
            [matRippleTrigger]="_getHostElement()"
        ></div>
        <div class="mat-button-focus-overlay"></div>
    `,
    styleUrls: ['button.scss'],
    inputs: ['disabled', 'color', 'icon', 'disableRipple', 'size'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AumButton
    extends _AumButtonMixinBase
    implements
        AfterViewInit,
        OnDestroy,
        CanDisable,
        CanColor,
        CanSize,
        CanIcon,
        OnInit,
        CanDisableRipple,
        FocusableOption
{
    override get disabled() {
        return this._disabled;
    }
    override set disabled(value: any) {
        this._disabled = coerceBooleanProperty(value);
    }

    constructor(
        el: ElementRef,
        private renderer: Renderer2,
        private _focusMonitor: FocusMonitor,
        @Optional() @Inject(ANIMATION_MODULE_TYPE) public _animationMode: string,
    ) {
        super(el);
        this.formatBox(el);
        this.formatFont(el);

        // For each of the variant selectors that is present in the button's host
        // attributes, add the correct corresponding class.
        for (const attr of BUTTON_HOST_ATTRIBUTES) {
            if (this._hasHostAttributes(attr)) {
                (this._getHostElement() as HTMLElement).classList.add(attr);
            }
        }

        if (this.isIconButton) {
            this['icon'] = DEFAULT_BUTTON_ICON;

            // Add icon
            const icon = this.renderer.createElement('i');
            this.renderer.appendChild(el.nativeElement, icon);
        }
    }

    static ngAcceptInputType_disabled: BooleanInput = false;
    static ngAcceptInputType_disableRipple: BooleanInput = false;

    private _disabled = false;

    /** Whether the button is icon button. */
    readonly isIconButton: boolean = this._hasHostAttributes('aum-icon-button');
    /** Reference to the MatRipple instance of the button. */
    @ViewChild(MatRipple, { static: false }) ripple: MatRipple;

    ngOnInit() {}

    ngAfterViewInit() {
        this._focusMonitor.monitor(this['_elementRef'], true);
    }

    ngOnDestroy() {
        this._focusMonitor.stopMonitoring(this['_elementRef']);
    }

    /** Focuses the button. */
    focus(origin?: FocusOrigin, options?: FocusOptions): void {
        if (origin) {
            this._focusMonitor.focusVia(this._getHostElement(), origin, options);
        } else {
            this._getHostElement().focus(options);
        }
    }

    _isRippleDisabled() {
        return this['disableRipple'] || this.disabled;
    }

    private formatBox(el: ElementRef) {}

    private formatFont(el: ElementRef) {
        el.nativeElement.style.fontFamily = MAIN_FONT;
        el.nativeElement.style.fontWeight = 'bold';
        el.nativeElement.style.fontSize = '14px';
        el.nativeElement.style.lineHeight = '150%';
    }

    /** Gets whether the button has one of the given attributes. */
    private _hasHostAttributes(...attributes: string[]) {
        return attributes.some((attribute) => this['_elementRef'].nativeElement.hasAttribute(attribute));
    }

    _getHostElement() {
        return this['_elementRef'].nativeElement;
    }
}

export function coerceBooleanProperty(value: any): boolean {
    return value != null && `${value}` !== 'false';
}
